import { useMemo } from 'react';

import { providers } from 'ethers';
import type { Account, Chain, Client, Transport } from 'viem';
import { useConfig, useConnectorClient } from 'wagmi';

import { WagmiConfig } from '@/config/wagmi.config';

/**
 * Methods only the connected wallet can answer. Everything else is a chain read that
 * any RPC can serve.
 */
const WALLET_METHODS = new Set([
  'eth_accounts',
  'eth_chainId',
  'eth_requestAccounts',
  'eth_sendTransaction',
  'eth_sign',
  'eth_signTransaction',
  'eth_signTypedData',
  'eth_signTypedData_v3',
  'eth_signTypedData_v4',
  'personal_sign',
]);

const isWalletMethod = (method: string) =>
  WALLET_METHODS.has(method) || method.startsWith('wallet_');

/**
 * ethers' `Web3Provider` sends *every* request to whatever provider it wraps, chain
 * reads included. viem does not work that way -- wagmi routes reads through the
 * configured `transports` and only wallet methods through the connector -- so handing
 * `Web3Provider` the connector transport puts reads on the wallet.
 *
 * Most wallets answer them anyway. Ledger's DApp Browser does not: `eth_blockNumber`
 * is dropped with no reply, so ethers blocks on its own bootstrap and never reaches
 * `eth_sendTransaction`. The call sits in the provider's pending map until its 240s
 * timeout fires, and the user sees a stake that never prompts.
 *
 * This restores the split viem does natively: signing on the wallet, reads on the
 * chain's configured RPC.
 *
 * viem's `request` is a strict per-method union while ethers wants a loose
 * `(args) => Promise<any>`, so the two are bridged structurally here.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
type LooseRequest = (args: { method: string; params?: any }) => Promise<any>;

function routeByMethod(wallet: LooseRequest, node: LooseRequest) {
  return {
    request: (args: { method: string; params?: any }) =>
      isWalletMethod(args.method) ? wallet(args) : node(args),
  };
}

export function clientToSigner(
  client: Client<Transport, Chain, Account>,
  nodeClient?: { request: any },
) {
  const { account, chain, transport } = client;

  if (!account || !chain || !transport) {
    return undefined;
  }

  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };

  const provider = new providers.Web3Provider(
    nodeClient
      ? routeByMethod(
          transport.request as LooseRequest,
          nodeClient.request as LooseRequest,
        )
      : (transport as any),
    network,
  );
  return provider.getSigner(account.address);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Action to convert a Viem Client to an ethers.js Signer. */
export function useEthersSigner({ chainId }: { chainId?: number } = {}) {
  const { data: client } = useConnectorClient<WagmiConfig>({ chainId });
  const config = useConfig();

  return useMemo(() => {
    if (!client) return undefined;

    // Public client for this chain, built from the `transports` in wagmi.config.
    const nodeClient = config.getClient({ chainId: client.chain.id });

    return clientToSigner(client, nodeClient);
  }, [client, config]);
}
